<template>
  <div class="sku-report poppins">
    <div class="ps-2 mb-6">
      <h3 class="sku-report__page-title">
        {{ reportStructure.properties.title }}
      </h3>
    </div>
    <!-- ── Page Header: Title + Filters ─────────────────────── -->
    <div class="sku-report__header-card">
      <div class="">
        <!-- Left: Title & Description -->

        <!-- Right: Filters -->
        <v-row align="end" class="ma-0" v-if="dateRangeTypes?.length">
          <v-col cols="12" sm="6" md="3" v-if="!$store.getters.isClient">
            <label class="sku-report__label">Client</label>
            <v-select
              v-model="selectedClient"
              :items="clients"
              item-text="text"
              item-value="index"
              dense
              outlined
              clearable
              :placeholder="$t('select_client')"
              hide-details
              class="sku-report__input"
            />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <label class="sku-report__label">Date Range</label>
            <DateTimePickerWithBorder
              @on-submit="getDateRange"
              position="center"
              :filter="{
                values: dateRangeTypes,
              }"
            />
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="3"
            class="d-flex align-end justify-end justify-md-start"
          >
            <button class="sku-report__apply-btn" @click="submitFilterValues">
              Apply
            </button>
          </v-col>
        </v-row>
      </div>
    </div>

    <!-- ── Loading State ─────────────────────────────────────── -->
    <template v-if="loading">
      <v-row class="sku-report__summary-row">
        <v-col cols="12" sm="6" md="3" v-for="n in 4" :key="'sk-c-' + n">
          <v-skeleton-loader type="card" height="100" />
        </v-col>
      </v-row>
      <v-skeleton-loader type="table" class="mt-4" />
    </template>

    <!-- ── Report Data (visible after search) ────────────────── -->
    <template v-if="!loading && reportData">
      <!-- ── Summary Counters ──────────────────────────────────── -->
      <v-row v-if="counters" class="sku-report__summary-row">
        <v-col
          v-for="(card, index) in counters.values"
          :key="'counter-' + index"
          cols="12"
          sm="6"
          :md="counterColSize"
        >
          <div class="sku-report__summary-card">
            <div
              class="sku-report__summary-icon-wrap"
              :style="{ background: summaryCards[index].bg }"
            >
              <v-icon size="24" :color="summaryCards[index].color">{{
                summaryCards[index].icon
              }}</v-icon>
            </div>
            <div class="sku-report__summary-info">
              <span class="sku-report__summary-label">{{ card.label }}</span>
              <span
                class="sku-report__summary-value d-flex align-items-center"
                v-html="card.value"
              />
            </div>
          </div>
        </v-col>
      </v-row>

      <!-- ── Two-Column Section ───────────────────────────────── -->
      <v-row v-if="columns" class="sku-report__columns-row">
        <v-col
          v-for="(col, ci) in columns.values"
          :key="'col-' + ci"
          cols="12"
          md="6"
        >
          <div class="sku-report__panel">
            <div class="sku-report__panel-header">
              <v-icon size="24" color="#7e04b7" class="mr-2">
                {{ ci === 0 ? "mdi-trophy-outline" : "mdi-chart-pie" }}
              </v-icon>
              <span class="sku-report__panel-title">{{ col.title }}</span>
            </div>
            <div class="sku-report__panel-body">
              <!-- Left column style: rank + progress + amount -->
              <template v-if="ci === 0">
                <div
                  v-for="(item, idx) in col.values"
                  :key="'sku-' + idx"
                  class="sku-report__sku-item"
                >
                  <div class="sku-report__sku-rank">{{ idx + 1 }}</div>
                  <div class="sku-report__sku-info">
                    <div class="sku-report__sku-name-row">
                      <span class="sku-report__sku-name">{{ item.sku }}</span>

                      <span
                        class="sku-report__sku-value d-flex align-items-center"
                        v-html="item.amount"
                      />
                    </div>
                    <v-progress-linear
                      :value="item.percentage"
                      height="12"
                      rounded
                      :color="progressColor(idx)"
                      background-color="#f1f5f9"
                      class="sku-report__sku-bar"
                    />
                  </div>
                </div>
              </template>

              <!-- Right column -->
              <template v-else>
                <div
                  v-for="(item, idx) in col.values"
                  :key="'sku-' + idx"
                  class="sku-report__sku-item"
                >
                  <div class="sku-report__sku-rank">{{ idx + 1 }}</div>
                  <div class="sku-report__sku-info">
                    <div class="sku-report__sku-name-row">
                      <span class="sku-report__sku-name">{{ item.sku }}</span>

                      <span
                        class="sku-report__sku-value d-flex align-items-center"
                      >
                        {{ item.percentage_label || item.percentage + "%" }}
                      </span>
                    </div>
                    <v-progress-linear
                      :value="item.percentage"
                      height="12"
                      rounded
                      :color="progressColor(idx)"
                      background-color="#f1f5f9"
                      class="sku-report__sku-bar"
                    />
                  </div>
                </div>
              </template>
            </div>
          </div>
        </v-col>
      </v-row>

      <!--       ── Data Table ───────────────────────────────────────── -->
      <div v-if="tableSection" class="sku-report__card sku-report__table-card">
        <div class="sku-report__card-header">
          <span class="sku-report__card-title">
            <v-icon size="24" color="#7e04b7" class="mr-1">mdi-table</v-icon>
            SKU Revenue Details
          </span>
          <v-spacer />
          <v-text-field
            v-model="tableSearch"
            dense
            outlined
            :placeholder="$t('search_sku')"
            hide-details
            prepend-inner-icon="mdi-magnify"
            class="sku-report__table-search"
          />
        </div>
        <v-data-table
          :headers="computedTableHeaders"
          :items="pagedTableItems"
          :items-per-page="-1"
          disable-sort
          hide-default-footer
          mobile-breakpoint="40"
          class="sku-report__data-table"
        >
          <template v-slot:item.sku="{ item }">
            <ClientColumn :value="{ name: item.sku }" />
          </template>
          <template v-slot:item.revenue="{ item }">
            <span v-html="item.revenue" />
          </template>
          <template v-slot:item.total_rev_order="{ item }">
            <span v-html="item.total_rev_order" />
          </template>
          <template v-slot:item.sku_rev_order="{ item }">
            <span v-html="item.sku_rev_order" />
          </template>
          <template v-slot:item.contribution="{ item }">
            <v-chip
              small
              :color="getContributionColor(parseFloat(item.contribution))"
              text-color="white"
              class="sku-report__chip font-weight-bolder max-w-60px d-flex align-items-center justify-content-center"
            >
              {{ item.contribution }}
            </v-chip>
          </template>
        </v-data-table>
        <Pagination
          class="mt-5"
          v-if="tableTotalItems > 0"
          :totalItems="tableTotalItems"
          :pageCount="tablePageCount"
          :currentPage="tablePage"
          :itemsPerPage="tableItemsPerPage"
          @page-changed="tablePage = $event"
          @items-per-page-changed="onTableItemsPerPageChange"
        />
      </div>
    </template>
  </div>
</template>

<script>
import { SET_PAGE_LOADING } from "@/core/services/store/config.module";
import ApiService from "@/core/services/api.service";
import DateTimePickerWithBorder from "@/own/components/DateTimePickerWithBorder.vue";
import Swal from "sweetalert2";
import ClientColumn from "@/own/components/datatable/ClientColumn.vue";
import Pagination from "@/own/components/pagination/Pagination.vue";

export default {
  name: "ReportRevenue",
  components: {
    ClientColumn,
    DateTimePickerWithBorder,
    Pagination,
  },
  data() {
    return {
      // ── Page config from API ─────────────────────────────────
      reportStructure: {
        properties: {
          title: "",
          description: "",
        },
        filters: [],
        export: false,
      },
      reportData: null,
      loading: false,
      tableSearch: "",

      // ── Table pagination ─────────────────────────────────────
      tablePage: 1,
      tableItemsPerPage: 10,

      // ── Filters ─────────────────────────────────────────────
      selectedClient: null,
      clients: [],
      selectedDateRangeType: null,
      dateRangeTypes: [],
      dateRange: [],
      filterData: {},
      filtersData: [],
    };
  },
  beforeMount() {
    this.getReportStructure();
  },
  watch: {
    tableSearch() {
      this.tablePage = 1;
    },
    computedTableItems() {
      this.tablePage = 1;
    },
  },
  computed: {
    summaryCards() {
      return [
        {
          title: "Total Revenue",
          value: "$128,430",
          icon: "mdi-cash-multiple",
          color: "#7e04b7",
          bg: "rgba(126,4,183,0.08)",
          trend: "+12.5%",
          trendIcon: "mdi-trending-up",
          trendColor: "#47be7d",
          trendClass: "sku-report__trend--up",
        },
        {
          title: "Total Orders",
          value: "3,842",
          icon: "mdi-cart-outline",
          color: "#5578eb",
          bg: "rgba(85,120,235,0.08)",
          trend: "+8.3%",
          trendIcon: "mdi-trending-up",
          trendColor: "#47be7d",
          trendClass: "sku-report__trend--up",
        },
        {
          title: "Avg Order Value",
          value: "$33.42",
          icon: "mdi-chart-line",
          color: "#47be7d",
          bg: "rgba(71,190,125,0.08)",
          trend: "-2.1%",
          trendIcon: "mdi-trending-down",
          trendColor: "#fd397a",
          trendClass: "sku-report__trend--down",
        },
        {
          title: "Total SKUs",
          value: "247",
          icon: "mdi-package-variant-closed",
          color: "#ff6b35",
          bg: "rgba(255,107,53,0.08)",
          trend: "+5 new",
          trendIcon: "mdi-plus",
          trendColor: "#47be7d",
          trendClass: "sku-report__trend--up",
        },
      ];
    },
    counters() {
      if (!this.reportData) return null;
      return this.reportData.find((s) => s.type === "counters") || null;
    },
    columns() {
      if (!this.reportData) return null;
      return this.reportData.find((s) => s.type === "columns") || null;
    },
    tableSection() {
      if (!this.reportData) return null;
      return this.reportData.find((s) => s.type === "table") || null;
    },
    counterColSize() {
      if (!this.counters) return 3;
      const count = this.counters.values.length;
      if (count <= 3) return 4;
      if (count === 4) return 3;
      return Math.max(2, Math.floor(12 / count));
    },
    computedTableHeaders() {
      if (!this.tableSection) return [];
      return this.tableSection.headers.map((h) => ({
        text: h,
        value: h
          .toLowerCase()
          .trim()
          .replace(/[^a-z0-9]+/g, "_") // spaces, /, -, etc.
          .replace(/^_+|_+$/g, ""), // remove start/end _
        class: "text-muted fw-bolder font-size-sm text-uppercase poppins-ls",
        cellClass: "second-text",
      }));
    },
    computedTableItems() {
      if (!this.tableSection) return [];
      return this.tableSection.body.map((row) => {
        const obj = {};
        row.forEach((cell, index) => {
          obj[`${this.computedTableHeaders[index].value}`] = cell;
        });
        return obj;
      });
    },
    filteredTableItems() {
      const q = (this.tableSearch || "").toLowerCase().trim();
      if (!q) return this.computedTableItems;
      return this.computedTableItems.filter((row) =>
        Object.values(row).some((val) =>
          String(val ?? "")
            .toLowerCase()
            .includes(q)
        )
      );
    },
    tableTotalItems() {
      return this.filteredTableItems.length;
    },
    tablePageCount() {
      return Math.ceil(this.tableTotalItems / this.tableItemsPerPage) || 1;
    },
    pagedTableItems() {
      const start = (this.tablePage - 1) * this.tableItemsPerPage;
      return this.filteredTableItems.slice(
        start,
        start + this.tableItemsPerPage
      );
    },
    commitmentChartSeries() {
      const values = this.columns.values[1].values || [];
      if (!values.length) return [];
      return values.map((item) => item.percentage);
    },
    commitmentChartOptions() {
      const values = this.columns.values[1].values || [];
      if (!values.length) return {};
      const colors = [
        "#7e04b7",
        "#4e51d2",
        "#47be7d",
        "#a855f7",
        "#ff6b35",
        "#68f7ab",
        "#5578eb",
        "#ffb822",
        "#6dd5a1",
        "#8993a4",
      ];
      return {
        chart: {
          type: "donut",
          fontFamily: "Poppins, sans-serif",
        },
        labels: values.map((item) => item.sku),
        colors: colors.slice(0, values.length),
        legend: {
          show: true,
          position: "bottom",
          fontSize: "12px",
          fontFamily: "Poppins, sans-serif",
          fontWeight: 500,
          labels: {
            colors: "#334155",
          },
          markers: {
            width: 10,
            height: 10,
            radius: 3,
          },
          itemMargin: {
            horizontal: 8,
            vertical: 4,
          },
        },
        dataLabels: {
          enabled: true,
          formatter: (val) => val.toFixed(1) + "%",
          style: {
            fontSize: "11px",
            fontFamily: "Poppins, sans-serif",
            fontWeight: 600,
            colors: ["#fff"],
          },
          dropShadow: {
            enabled: false,
          },
        },
        plotOptions: {
          pie: {
            donut: {
              size: "55%",
              labels: {
                show: true,
                name: {
                  show: true,
                  fontSize: "13px",
                  fontFamily: "Poppins, sans-serif",
                  fontWeight: 600,
                  color: "#1e293b",
                },
                value: {
                  show: true,
                  fontSize: "20px",
                  fontFamily: "Poppins, sans-serif",
                  fontWeight: 700,
                  color: "#1e293b",
                  formatter: (val) => parseFloat(val).toFixed(1) + "%",
                },
                total: {
                  show: true,
                  label: "Total",
                  fontSize: "12px",
                  fontFamily: "Poppins, sans-serif",
                  fontWeight: 500,
                  color: "#94a3b8",
                  formatter: (w) => {
                    const total = w.globals.seriesTotals.reduce(
                      (a, b) => a + b,
                      0
                    );
                    return total.toFixed(1) + "%";
                  },
                },
              },
            },
          },
        },
        stroke: {
          width: 2,
          colors: ["#fff"],
        },
        tooltip: {
          enabled: true,
          y: {
            formatter: (val) => val.toFixed(1) + "%",
          },
          style: {
            fontSize: "12px",
            fontFamily: "Poppins, sans-serif",
          },
        },
        responsive: [
          {
            breakpoint: 600,
            options: {
              chart: { height: 300 },
              legend: { position: "bottom", fontSize: "10px" },
            },
          },
        ],
      };
    },
  },
  methods: {
    onTableItemsPerPageChange(val) {
      this.tableItemsPerPage = val;
      this.tablePage = 1;
    },
    dateFormatter(date, charForJoin = "-") {
      return [
        date.getFullYear(),
        date.getMonth() + 1 > 9
          ? date.getMonth() + 1
          : `0${date.getMonth() + 1}`,
        date.getDate() > 9 ? date.getDate() : `0${date.getDate()}`,
      ].join(charForJoin);
    },
    today() {
      const d = new Date();
      return this.dateFormatter(d);
    },
    getDateRange(dateRange, selectedDateRangeType) {
      // console.log(dateRange, selectedDateRangeType);
      this.dateRange = dateRange;
      this.selectedDateRangeType = selectedDateRangeType;
      this.submitFilterValues();
      // this.updateDataOfCharts(this.filterData);
    },
    getReportStructure() {
      this.$store.commit(SET_PAGE_LOADING, true);
      ApiService.post("/reports/reportRevenue")
        .then(({ data }) => {
          this.reportStructure = data.data;
          if (!this.$store.getters.isClient) {
            // Populate clients dropdown from the "client" filter
            const clientFilter = data.data.filters
              ? data.data.filters.find((f) => f.name === "client")
              : null;
            if (clientFilter && clientFilter.values) {
              this.clients = clientFilter.values;
            }
          }
          // Populate date range type dropdown from the "date_range" filter
          const dateRangeFilter = data.data.filters
            ? data.data.filters.find((f) => f.name === "date_range")
            : null;
          if (dateRangeFilter && dateRangeFilter.values) {
            this.dateRangeTypes = dateRangeFilter.values;
            // Default to the first option
            this.selectedDateRangeType = dateRangeFilter.values[0].index;
          }
        })
        .finally(() => {
          this.$store.commit(SET_PAGE_LOADING, false);
        });
    },
    buildFiltersPayload() {
      const filters = [];
      if (!this.$store.getters.isClient && this.selectedClient) {
        filters.push({
          name: "client",
          value: [this.selectedClient],
        });
      }
      if (this.dateRange && this.dateRange.length === 2) {
        filters.push({
          name: "date_range.date",
          value: this.dateRange,
        });
      }
      if (this.selectedDateRangeType) {
        filters.push({
          name: "date_range.select",
          value: this.selectedDateRangeType,
        });
      }
      return filters;
    },
    getReportData() {
      this.loading = true;
      const payload = {
        filters: this.buildFiltersPayload(),
      };
      ApiService.post("/reports/reportRevenue/show", payload)
        .then(({ data }) => {
          this.reportData = data.data;
        })
        .catch(() => {
          this.reportData = null;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    submitFilterValues() {
      // Validate: at least one filter must be selected
      const hasDateRange =
        this.dateRange &&
        this.dateRange.length === 2 &&
        this.dateRange[0] &&
        this.dateRange[1];
      if (!this.$store.getters.isClient && !this.selectedClient) {
        Swal.fire("Warning!", "Please select client.", "warning");
        return;
      } else if (!hasDateRange) {
        Swal.fire("Warning!", "Please select date range", "warning");
        return;
      }
      this.getReportData();
    },
    // resetFilterValues() {
    //   this.$store.commit(SET_PAGE_LOADING, true);
    //   if (this.$refs.filter && this.$refs.filter.$refs.filter) {
    //     this.$refs.filter.$refs.filter.forEach((f) => {
    //       f.clearData();
    //     });
    //   }
    //   this.filtersData = [];
    //   this.reportData = null;
    //   this.$store.commit(SET_PAGE_LOADING, false);
    // },
    // setTableFiltersValues(val) {
    //   this.filtersData = val;
    // },
    getContributionColor(pct) {
      if (pct >= 50) return "#7e04b7";
      if (pct >= 35) return "#a855f7";
      if (pct >= 25) return "#5578eb";
      if (pct >= 10) return "#47be7d";
      if (pct >= 5) return "#ffb822";
      if (pct >= 2) return "#ff6b35";
      return "#8993a4";
    },
    progressColor(idx) {
      const colors = [
        // "#7e04b7",
        // "#4e51d2",
        // "#47be7d",
        // "#a855f7",
        // "#ff6b35",
        // "#68f7ab",
        // "#5578eb",
        // "#ffb822",
        // "#6dd5a1",
        // "#8993a4",
        "#7e04b7",
        "#9333ea",
        "#a855f7",
        "#b47de8",
        "#5578eb",
        "#6895f7",
        "#47be7d",
        "#6dd5a1",
        "#ff6b35",
        "#ffb822",
      ];
      return colors[idx % colors.length];
    },
    commitmentColor(idx) {
      const colors = [
        "#7e04b7",
        "#9333ea",
        "#a855f7",
        "#b47de8",
        "#5578eb",
        "#6895f7",
        "#47be7d",
        "#6dd5a1",
        "#ff6b35",
        "#ffb822",
      ];
      return colors[idx % colors.length];
    },
  },
};
</script>

<style lang="scss" scoped>
/* ── Variables ──────────────────────────────────────────────── */
$primary: #7e04b7;
$border: rgba(0, 0, 0, 0.06);
$radius: 14px;
$radius-sm: 10px;
$radius-xs: 8px;
$shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 4px 16px rgba(0, 0, 0, 0.04);
$font: "Poppins", sans-serif;
$title-font-size: 17px;
$value-font-size: 15px;

/* ── Page wrapper ───────────────────────────────────────────── */
.sku-report {
  background-color: #f7f7fa !important;
  padding: 0px 32px 40px;
  font-family: $font;

  @media (max-width: 960px) {
    padding: 8px 16px 32px;
  }
  @media (max-width: 600px) {
    padding: 8px 12px 24px;
  }
}

/* ── Header Card (Title + Filters) ──────────────────────────── */
.sku-report__header-card {
  background: #fff;
  border-radius: $radius;
  box-shadow: $shadow;
  border: 1px solid $border;
  padding: 20px 24px 24px;
  margin-bottom: 24px;

  @media (max-width: 600px) {
    padding: 16px 14px 18px;
  }
}

.sku-report__header-row {
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;

  @media (max-width: 960px) {
    flex-direction: column;
  }
}

.sku-report__header-left {
  flex-shrink: 0;
}

.sku-report__page-title {
  font-family: $font;
  font-size: 22px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 4px;
  letter-spacing: -0.3px;
}

.sku-report__page-desc {
  font-family: $font;
  font-size: 14px;
  font-weight: 400;
  color: #64748b;
  margin: 0;
}

.sku-report__header-right {
  flex: 1;
  min-width: 0;
}

.sku-report__apply-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 9px 30px;
  background: $primary;
  color: #fff;
  font-family: $font;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.9px;
  border: none;
  border-radius: $radius-xs;
  cursor: pointer;
  transition: background 0.2s ease, box-shadow 0.2s ease;
  white-space: nowrap;
  margin-bottom: 2px;

  &:hover {
    background: darken($primary, 8%);
    box-shadow: 0 2px 8px rgba($primary, 0.3);
  }

  &:active {
    transform: scale(0.97);
  }
}

.sku-report__label {
  display: block;
  font-family: $font;
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
}

.sku-report__input ::v-deep .v-input__slot {
  border-radius: $radius-sm !important;
  min-height: 40px !important;
}

/* ── Generic Card ──────────────────────────────────────────── */
.sku-report__card {
  background: #ffffff;
  border-radius: $radius;
  box-shadow: $shadow;
  border: 1px solid $border;
  overflow: hidden;
}

.sku-report__card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px;
  //border-bottom: 1px solid $border;
  flex-wrap: wrap;
  gap: 10px;
}

.sku-report__card-title {
  font-family: $font;
  font-size: $title-font-size;
  font-weight: 600;
  color: #1e293b;
  display: flex;
  align-items: center;
}

/* ── Table Section ──────────────────────────────────────────── */
.sku-report__table-card {
  margin-bottom: 40px;
}

.sku-report__table-search {
  max-width: 220px;

  ::v-deep .v-input__slot {
    border-radius: $radius-sm !important;
    min-height: 36px !important;
  }

  @media (max-width: 600px) {
    max-width: 100%;
    width: 100%;
  }
}

.sku-report__data-table {
  font-family: $font;

  ::v-deep {
    thead th {
      &:first-of-type {
        padding-inline-start: 32px !important;
      }
      //background: #f8fafc !important;
      border-bottom: 1px solid $border !important;
      white-space: nowrap;
    }

    tbody td {
      //&:first-of-type {
      //  padding-inline-start: 30px !important;
      //}

      font-family: $font !important;
      font-size: $value-font-size !important;
      border-bottom: 1px solid rgba(0, 0, 0, 0.04) !important;
      padding-top: 12px !important;
      padding-bottom: 12px !important;

      img.icon {
        height: 1.1rem;
        margin-inline-end: 3px;
        margin-bottom: 2px;
        vertical-align: middle;
      }
    }

    tbody tr:hover {
      background: rgba($primary, 0.02) !important;
    }

    .v-data-footer {
      font-family: $font;
      font-size: $value-font-size;
      border-top: 1px solid $border;
    }
  }
}

/* ── Summary Cards ──────────────────────────────────────────── */
.sku-report__summary-row {
  margin-bottom: 0px;
}

.sku-report__summary-card {
  background: #fff;
  border-radius: $radius;
  box-shadow: $shadow;
  border: 1px solid $border;
  padding: 20px;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  transition: box-shadow 0.25s ease, transform 0.25s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }
}

.sku-report__summary-icon-wrap {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.sku-report__summary-info {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.sku-report__summary-label {
  font-family: $font;
  font-size: 12px;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 4px;
}

.sku-report__summary-value {
  font-family: $font;
  font-size: 22px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.2;

  ::v-deep .icon {
    width: 22px;
    height: 22px;
    vertical-align: middle;
    margin-inline-end: 4px;
  }
}

/* ── Panels (shared for both columns) ───────────────────────── */
.sku-report__columns-row {
  margin-bottom: 24px;
  margin-top: 0px;
}

.sku-report__panel {
  background: #fff;
  border-radius: $radius;
  box-shadow: $shadow;
  border: 1px solid $border;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.sku-report__panel-header {
  display: flex;
  align-items: center;
  padding: 18px 20px;
  border-bottom: 1px solid $border;
}

.sku-report__panel-title {
  font-family: $font;
  font-size: $title-font-size;
  font-weight: 600;
  color: #1e293b;
}

.sku-report__panel-body {
  padding: 16px 20px 20px;
  flex: 1;

  @media (max-width: 600px) {
    padding: 12px 14px 16px;
  }
}

/* ── Top SKU Revenue List ───────────────────────────────────── */
.sku-report__sku-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  &:first-child {
    padding-top: 0;
  }
}

.sku-report__sku-rank {
  width: 26px;
  height: 26px;
  border-radius: 8px;
  background: rgba($primary, 0.08);
  color: $primary;
  font-family: $font;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.sku-report__sku-info {
  flex: 1;
  min-width: 0;
}

.sku-report__sku-name-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.sku-report__sku-name {
  font-family: $font;
  font-size: $value-font-size;
  font-weight: 600;
  color: #1e293b;
}

.sku-report__sku-value {
  font-family: $font;
  font-size: $value-font-size;
  font-weight: 700;
  color: #1e293b;

  ::v-deep {
    img.icon {
      height: 1.1rem;
      margin-inline-end: 3px;
      margin-bottom: 2px;
      vertical-align: middle;
    }
  }
}

.sku-report__sku-bar {
  border-radius: 4px;
}

/* ── Revenue Commitments List ───────────────────────────────── */
.sku-report__commitment-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  &:first-child {
    padding-top: 0;
  }
}

.sku-report__commitment-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.sku-report__commitment-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.sku-report__commitment-name {
  font-family: $font;
  font-size: $value-font-size;
  font-weight: 600;
  color: #1e293b;
}

.sku-report__commitment-right {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 140px;
}

.sku-report__commitment-pct {
  font-family: $font;
  font-size: $value-font-size;
  font-weight: 700;
  color: #1e293b;
  min-width: 45px;
  text-align: end;
}

.sku-report__commitment-bar {
  flex: 1;
  min-width: 60px;
}
.sku-report__chart-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 380px;

  //::v-deep .apexcharts-legend {
  //  padding-top: 24px !important;
  //  display: flex !important;
  //  flex-wrap: wrap !important;
  //  justify-content: center !important;
  //  gap: 12px 24px !important;
  //}
  //
  //::v-deep .apexcharts-legend-series {
  //  margin: 0 !important;
  //  display: flex !important;
  //  align-items: center !important;
  //  width: 140px; /* Forces a neat grid layout */
  //}
  //
  //::v-deep .apexcharts-legend-marker {
  //  margin-inline-end: 8px !important;
  //}
}
</style>
